import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import styles from './PatientMessage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import PatientListGroup from './PatientListGroup';
import { setChatList, setNewChat, setRecipient, setReloadMessageList, setSelectedIndex, setChatVisible } from './PatientMessageSlicer';
import useAxios from '../../services/AxiosConfig';
import ChatContainer from '../../components/ChatContainer';
import { useWebSocket } from '../../services/webSocket/useWebSocket';
import { setCallInformation, openCallContainer } from '../../slicers/CallContainerSlice';


const PatientMessageList = ({ onCloseClick, patients }) => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userInfoState = useSelector((state) => state.userInfo);
    const userId = userInfoState.id;
    const userFullName = userInfoState.fullName;
    const patientMessage = useSelector((state) => state.patientMessage);
    const isChatVisible = patientMessage.isChatVisible;
    const recipient = patientMessage.recipient;
    const videoAndMessageSlicer = useSelector((state) => state.videoAndMessage);
    const filterChatSlice = useSelector((state) => state.filtersChat);
    const isVideoCallVisible = videoAndMessageSlicer.isVideoCallVisible;

    const [originalDataList, setOriginalDataList] = useState([]);
    const { receivedMessage } = useWebSocket();

    const callContainerShowing = useSelector((state) => state.callContainer.callContainerShowing);


    const getAllMessageByUserId = useCallback(async () => {
        try {
            const responseData = await httpService.get(`chime/getAllMessageByUserId?userId=${userId}&recipientType=PATIENT`);
            const messages = responseData.data.getAllMessage || [];
            setOriginalDataList(messages);
            dispatch(setReloadMessageList(false));
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [userId, dispatch, httpService]);

    const searchMessages = (arr, id) => {
        return arr.flat().filter(message => message.senderId === id || message.recipientId === id);
    };


    useEffect(() => {
        getAllMessageByUserId();
    }, [getAllMessageByUserId]);

    //If the recipient changes make sure to set the chat reflects the change.
    useEffect(() => {
        var newRecipient = searchMessages(originalDataList, recipient.id);
        if (newRecipient.length > 0) {
            dispatch(setChatList(newRecipient));
            dispatch(setSelectedIndex(newRecipient[0].channelArn));
        }
    }, [recipient, originalDataList]);



    useEffect(() => {
        if (patientMessage.selectedIndex !== -1) {
            const chatListMessages = originalDataList.find(array =>
                array.length > 0 && array[0].channelArn === patientMessage.selectedIndex
            );
            dispatch(setChatList(chatListMessages || []));
        }
    }, [patientMessage.selectedIndex, originalDataList, dispatch]);

    useEffect(() => {
        if (receivedMessage || patientMessage.reloadMessageList) {
            getAllMessageByUserId();
        }
    }, [receivedMessage, patientMessage.reloadMessageList]);

    // Refetch when user returns to this tab/window so list stays in sync with other devices
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                getAllMessageByUserId();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [getAllMessageByUserId]);


    const handleItemClick = (item) => {
        if (callContainerShowing) {
            return;
        }
        dispatch(setSelectedIndex(item[0].channelArn));
        dispatch(setChatList(item));
        dispatch(setNewChat(false));
        const messageFromUser = item.find(message => message.senderId === userId);
        if (messageFromUser) {
            dispatch(setRecipient({ 'id': parseInt(messageFromUser.recipientId), name: messageFromUser.recipientName }));
        } else {
            const messageToUser = item.find(message => message.recipientId === userId);
            if (messageToUser) {
                dispatch(setRecipient({ 'id': parseInt(messageToUser.senderId), name: messageToUser.senderName }));
            }
        }
        dispatch(setChatVisible(true));
    };

    const handleChatContainerClose = () => {
        onCloseClick();
    };

    const handleStartCall = (audioOnly) => {
        const callData = {
            title: `Patient Message Call - ${userFullName} & ${recipient.name}`,
            appointmentId: null,
            senderId: userId,
            recipientId: recipient.id,
            senderName: userFullName,
            recipientName: recipient.name
        };
        dispatch(setSelectedIndex(-1));
        dispatch(setChatList([]));
        dispatch(setChatVisible(false));
        dispatch(setCallInformation({ "audioOnly": audioOnly, "callData": callData, viewType: "patientMessage" }));
        dispatch(openCallContainer());
    };

    const handleListStartCall = (item, audioOnly) => {
        let id, name;
        const messageFromUser = item.find(message => message.senderId === userId);
        if (messageFromUser) {
            name = messageFromUser.recipientName;
            id = parseInt(messageFromUser.recipientId);
        } else {
            const messageToUser = item.find(message => message.recipientId === userId);
            if (messageToUser) {
                name = messageToUser.senderName;
                id = parseInt(messageToUser.senderId);
            }
        }
        const callData = {
            title: `Patient Message ${audioOnly ? "Audio" : "Video"} Call - ${userFullName} & ${name}`,
            appointmentId: null,
            senderId: userId,
            recipientId: id,
            senderName: userFullName,
            recipientName: name
        };
        dispatch(setSelectedIndex(-1));
        dispatch(setChatList([]));
        dispatch(setChatVisible(false));
        dispatch(setCallInformation({ "audioOnly": audioOnly, "callData": callData, viewType: "patientMessage" }));
        dispatch(openCallContainer());
    };

    const deleteChannelMembershipRequest = async (item) => {
        const params = {
            channelArn: item[0].channelArn,
            userId: userId
        };
        try {
            await httpService.post('chime/deleteChannelMembershipRequest', params);

            // Update the state to remove the deleted item
            const updatedOriginalDataList = originalDataList.filter(message => message[0].channelArn !== item[0].channelArn);
            setOriginalDataList(updatedOriginalDataList);
        } catch (error) {
            console.error('Error deleting channel membership:', error);
        }
    };

    const filteredMessages = useMemo(() => {
        if (!Array.isArray(originalDataList) || originalDataList.length === 0) {
            return [];
        }

        const filtered = originalDataList.filter((item) => {  
            const patient = patients.find(patient => patient.id === item[0].recipientId || patient.id === item[0].senderId);
            if (!patient) {
                return false;
            }
            const searchText = filterChatSlice.searchText.toLowerCase();
            const matchesSearch = patient.name.toLowerCase().includes(searchText) || item[0].message.toLowerCase().includes(searchText);

            const matchesOrganization = filterChatSlice.selectedOrganization === '' || patient['organizationIds']?.includes((Number(filterChatSlice.selectedOrganization)));
            const matchesGroup = filterChatSlice.selectedGroup === '' || patient['groupIds']?.includes(Number(filterChatSlice.selectedGroup)) || patient['groupIds']?.length === 0;
            const matchesLocation = filterChatSlice.selectedLocation === '' || patient['subGroupIds']?.includes(Number(filterChatSlice.selectedLocation)) || patient['subGroupIds']?.length === 0;
            const matchesTeam = filterChatSlice.selectedTeam === '' || patient['teamIds']?.includes(Number(filterChatSlice.selectedTeam)) || patient['teamIds']?.length === 0;;
            return matchesOrganization && matchesSearch && matchesGroup && matchesLocation && matchesTeam;

        });
        return [...filtered].sort((a, b) => new Date(b[0].createdTime) - new Date(a[0].createdTime));
    }, [patients, originalDataList, filterChatSlice.searchText, filterChatSlice.selectedOrganization, filterChatSlice.selectedGroup, filterChatSlice.selectedLocation, filterChatSlice.selectedTeam]);



    return (
        <div style={{ flexGrow: "1", height: 0, display: "flex" }}>
            <div style={{ flexGrow: "1", height: "100%", overflow: "auto" }}>
                <PatientListGroup
                    items={filteredMessages}
                    onStartCall={handleListStartCall}
                    onDeleteChannelMembership={deleteChannelMembershipRequest}
                    onItemClick={handleItemClick}
                />
            </div>
            {isChatVisible && !isVideoCallVisible && (
                <Form className={styles.ChatContainer}>
                    <ChatContainer view='patientMessage'
                        handleClose={handleChatContainerClose}
                        onStartVideoCall={() => handleStartCall(false)}
                        onStartAudioCall={() => handleStartCall(true)} />
                </Form>
            )}
        </div>
    );
};

export default PatientMessageList;
